import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useHistory, Prompt } from "react-router-dom";
import DateFnsUtils from "@date-io/date-fns";

// Material
import { makeStyles } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import Paper from "@material-ui/core/Paper";

// Formik
import { Formik, Form } from "formik";

// Config
import { capturePages } from "../../config/values";
import colors from "../../config/colors";
import styles from "../../config/styles";

// Hooks
import useTranslate from "../../hooks/useTranslate";

// Components
import Button from "../Button";
import ContentCardHeader from "../ContentCardHeader";
import EditMediaGallery from "../EditMediaGallery";
import InputList from "../Input/InputList";
import Text from "../Text";

const useStyles = makeStyles(theme => ({
  root: {
    border: styles.values.border
  },
  title: {
    fontSize: 24
  },
  cancelButton: {
    height: 42,
    width: 160,
    border: styles.values.border,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderColor: colors.riverBed,
    borderRadius: theme.shape.borderRadius
  },
  cancelText: {
    color: colors.riverBed
  },
  formContent: {
    padding: theme.spacing(3.5, 5),
    display: "flex",
    flexDirection: "column",
    borderBottom: styles.values.border
  },
  inputItem: {
    marginBottom: theme.spacing(4),
    display: "flex",
    flexDirection: "column"
  },
  buttonContainer: {
    padding: theme.spacing(4, 5)
  },
  submit: {
    width: "100%"
  }
}));

// The shared shell for both EditInjuryForm and EditPhysicalDamageForm - header
// + Cancel, one InputList per entry in `fields`, an optional extraSection
// (injury's category/body-map picker, null for physical damage), the image
// gallery, and a single Done button. Neither the fields' content/text nor the
// submit dispatch are known here - both are owned by the caller, same as
// DetailValue's onAdd/onUpdate split.
const EditMediaForm = ({
  caseId,
  itemId,
  page,
  title,
  exitPromptText,
  fields,
  extraSection,
  images,
  imageHeaders,
  loadingHeaders,
  isDeletingImage,
  isDraft,
  initialValues,
  onSubmit,
  detailRoute
}) => {
  const classes = useStyles();
  const getText = useTranslate();
  const history = useHistory();
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (leaving) history.push(detailRoute);
  }, [detailRoute, history, leaving]);

  const goBack = () => setLeaving(true);

  const handleSubmit = (values, formikHelpers) => {
    onSubmit(values, { ...formikHelpers, goBack });
  };

  return (
    <Paper elevation={0} className={classes.root}>
      <ContentCardHeader>
        <Text className={classes.title} type="heading2">
          {title}
        </Text>
        <Button onClick={goBack} className={classes.cancelButton}>
          <Text
            className={classes.cancelText}
            text="common.prompts.cancel"
            type="contentLabel3"
          />
        </Button>
      </ContentCardHeader>
      <Prompt message={() => exitPromptText} when={!leaving} />
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validateOnBlur={false}
        >
          {({ errors, isSubmitting, setValues, submitForm, values }) => (
            <Form>
              <div className={classes.formContent}>
                {fields.map(field => (
                  <InputList
                    key={field.name}
                    addButtonText={field.addButtonText}
                    canAddMore={!isDraft}
                    className={classes.inputItem}
                    constantValues={field.constantValues}
                    error={errors[field.name]}
                    label={field.label}
                    multiline
                    name={field.name}
                    placeholder={field.placeholder}
                    title={field.title}
                    values={values[field.name]}
                  />
                ))}

                {extraSection && extraSection({ values, errors, setValues })}

                {images && (
                  <EditMediaGallery
                    caseId={caseId}
                    imageHeaders={imageHeaders}
                    images={images}
                    isDeletingImage={isDeletingImage}
                    itemId={itemId}
                    loadingHeaders={loadingHeaders}
                    page={page}
                  />
                )}
              </div>

              <div className={classes.buttonContainer}>
                <Button
                  className={classes.submit}
                  color="primary"
                  disabled={isSubmitting}
                  disableElevation
                  onClick={submitForm}
                  type="submit"
                  variant="contained"
                >
                  {getText("common.prompts.done")}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </MuiPickersUtilsProvider>
    </Paper>
  );
};

EditMediaForm.propTypes = {
  caseId: PropTypes.string.isRequired,
  itemId: PropTypes.string.isRequired,
  page: PropTypes.oneOf(Object.values(capturePages)).isRequired,
  title: PropTypes.string.isRequired,
  exitPromptText: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      title: PropTypes.string,
      label: PropTypes.string,
      placeholder: PropTypes.string,
      addButtonText: PropTypes.string,
      constantValues: PropTypes.arrayOf(PropTypes.shape({}))
    })
  ).isRequired,
  extraSection: PropTypes.func,
  images: PropTypes.arrayOf(PropTypes.shape({})),
  imageHeaders: PropTypes.shape({}).isRequired,
  loadingHeaders: PropTypes.shape({}).isRequired,
  isDeletingImage: PropTypes.string,
  isDraft: PropTypes.bool.isRequired,
  initialValues: PropTypes.shape({}).isRequired,
  onSubmit: PropTypes.func.isRequired,
  detailRoute: PropTypes.string.isRequired
};

EditMediaForm.defaultProps = {
  extraSection: null,
  images: null,
  isDeletingImage: ""
};

export default EditMediaForm;
